/**
 * The order of a finished table's rows (spec section 2, sorting and empty
 * rows), as its plan asks (`TablePlan.sort`, `TablePlan.hideEmpty`): the
 * answer rows nobody gave left out, and the answer rows sorted by the Totals
 * column, highest first, within the runs between nets and statistic rows. A
 * grid summary's rows sort the same way. Applied once the table is built
 * (`buildResult`), before it is shown, kept or exported. Pure.
 */

import type { AxisLeaf, ModelCell, TableModel } from "utils/tableModel"
import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { DownRow, downRowsOf, withDownRows } from "features/Dashboard/runtime/downRows"
import type { RowSlot, TablePlan } from "../compiler/table"

/** The rows one slot of the layout became: every statistic line, its bases and its letters. */
interface Block {
  /** The slot they were built from; null for rows the layout does not name, which never move. */
  slot: RowSlot | null
  rows: DownRow[]
}

/**
 * What moves as one: an answer row; a grid row of a summary with every slot
 * it has (a summary of means shows one slot per statistic, all of the same
 * field); or anything else, which stays where it is and ends a run.
 */
interface Unit {
  kind: "answer" | "grid-row" | "other"
  blocks: Block[]
  /** An answer row that stays at the end of its run, in its order. */
  fixed: boolean
}

/** What an answer row is sorted by: its column %, which is weighted when the table is, else its count. */
const SORT_STATS: string[] = [STATISTIC_TYPE.COL_PERCENT, STATISTIC_TYPE.BIG_N, STATISTIC_TYPE.N]
/** What says nobody gave an answer: its count, else its weighted count or its column %. */
const COUNT_STATS: string[] = [STATISTIC_TYPE.N, STATISTIC_TYPE.BIG_N, STATISTIC_TYPE.COL_PERCENT]

const numeric = (cell: ModelCell | undefined): number | null =>
  cell && typeof cell.raw === "number" && Number.isFinite(cell.raw) ? cell.raw : null

/**
 * The column the rows are judged by: the Totals, or the one column when the
 * engine left only one (facts.md, fact 7). -1 when there is neither.
 */
const totalsColumn = (model: TableModel): number => {
  const total = model.across.leaves.findIndex((leaf) => leaf.isTotal)
  return total >= 0 ? total : model.across.leaves.length === 1 ? 0 : -1
}

/** The first of `stats` that `rows` has a line of, its value in column `col`; null without one. */
const valueAt = (rows: DownRow[], stats: string[], col: number): number | null => {
  for (const stat of stats) {
    const line = rows.find((row) => row.leaf.stat === stat)
    if (line) return numeric(line.cells[col])
  }
  return null
}

/** The rows of the model, one list per block (`elementIndex`): what one slot of the layout became. */
const blocksOf = (rows: DownRow[]): DownRow[][] =>
  rows.reduce<DownRow[][]>((out, row) => {
    const last = out[out.length - 1]
    if (last && last[0].leaf.elementIndex === row.leaf.elementIndex) last.push(row)
    else out.push([row])
    return out
  }, [])

/** Whether rows that start with `leaf` were built from `slot`: the Base is the Totals, any other slot shows its name. */
const builtFrom = (slot: RowSlot, leaf: AxisLeaf): boolean =>
  slot.kind === "base"
    ? leaf.isTotal
    : !leaf.isTotal && leaf.path.length === 1 && leaf.path[0] === slot.display

/**
 * Each block with the slot it was built from, each slot used once. A table
 * as built has its blocks in layout order, so each is looked for after the
 * last one found first (a mean and an answer may show the same name); a
 * slot the table left out (a value nobody gave, a mean the check took away)
 * built no block, and is passed over. In a table whose rows were already
 * moved, a block is looked for anywhere.
 */
const withSlots = (blocks: DownRow[][], layout: RowSlot[]): Block[] => {
  const used = new Set<number>()
  let at = 0
  return blocks.map((rows) => {
    const fits = (slot: RowSlot, i: number): boolean => !used.has(i) && builtFrom(slot, rows[0].leaf)
    let k = layout.findIndex((slot, i) => i >= at && fits(slot, i))
    if (k < 0) k = layout.findIndex(fits)
    if (k < 0) return { slot: null, rows }
    used.add(k)
    at = k + 1
    return { slot: layout[k], rows }
  })
}

/** The grid row a slot of a summary shows (an NPS row by its promoters' field); null for any other slot. */
const gridRowOf = (slot: RowSlot | null): string | null => {
  if (slot?.kind === "stat") return slot.field !== null && slot.sum !== "total" ? slot.field : null
  if (slot?.kind === "difference") return slot.fields ? slot.plus : null
  return null
}

/** The blocks as what moves: a grid row's slots together, every other block on its own. */
const unitsOf = (blocks: Block[]): Unit[] =>
  blocks.reduce<Unit[]>((units, block) => {
    const { slot } = block
    const row = gridRowOf(slot)
    const last = units[units.length - 1]
    if (row !== null && last?.kind === "grid-row" && gridRowOf(last.blocks[0].slot) === row) {
      last.blocks.push(block)
      return units
    }
    const answer = slot?.kind === "row" && !slot.net
    units.push({
      kind: answer ? "answer" : row !== null ? "grid-row" : "other",
      blocks: [block],
      // A value of a number is in the order of the numbers, as a range is.
      fixed: slot?.kind === "row" && (slot.fixed === true || slot.value === true),
    })
    return units
  }, [])

/**
 * Whether nobody gave the answer of `unit` (its count in the Totals is 0),
 * or nobody answered the grid row (its base, when the table shows it). The
 * table leaves out a grid row nobody answered as it builds it, where the
 * base is known whether shown or not.
 */
const nobodyGave = (unit: Unit, col: number): boolean => {
  const rows = unit.blocks.flatMap((b) => b.rows)
  if (unit.kind === "answer") return valueAt(rows, COUNT_STATS, col) === 0
  if (unit.kind === "grid-row") return valueAt(rows, [STATISTIC_TYPE.BASE_N], col) === 0
  return false
}

/**
 * What `unit` is sorted by in column `col`: an answer by its column %; a
 * grid row by what its first slot shows (a share, a mean, a sum, NPS).
 */
const sortValue = (unit: Unit, col: number): number | null => {
  const [first] = unit.blocks
  if (unit.kind === "answer") return valueAt(first.rows, SORT_STATS, col)
  const slot = first.slot
  if (slot?.kind === "stat") return valueAt(first.rows, [slot.stat], col)
  if (slot?.kind === "difference") return valueAt(first.rows, [STATISTIC_TYPE.PERC], col)
  return null
}

/**
 * One run, sorted: the rows that move, highest first (a blank last), in
 * their order on a tie; then the fixed rows, in their order.
 */
const sortRun = (run: Unit[], col: number): Unit[] => {
  const ranked = run
    .filter((u) => !u.fixed)
    .map((u, i) => ({ u, i, v: sortValue(u, col) }))
    .sort((a, b) =>
      a.v === b.v ? a.i - b.i : a.v === null ? 1 : b.v === null ? -1 : b.v - a.v
    )
  return [...ranked.map((r) => r.u), ...run.filter((u) => u.fixed)]
}

/** Every run of answer rows or grid rows sorted; whatever stands between them stays where it is. */
const sortRuns = (units: Unit[], col: number): Unit[] => {
  const out: Unit[] = []
  let run: Unit[] = []
  const flush = (): void => {
    out.push(...sortRun(run, col))
    run = []
  }
  units.forEach((u) => {
    if (u.kind !== "other") run.push(u)
    else {
      flush()
      out.push(u)
    }
  })
  flush()
  return out
}

/**
 * `model` with its rows as `plan` orders them. With hideEmpty, an answer row
 * whose count in the Totals is 0 is left out (an exception's too); nets,
 * statistic rows, differences and the Base never are. With sort "desc",
 * each run of answer rows (or of a grid summary's rows) between anything
 * else is sorted by the Totals, the fixed rows at its end (`RowSlot.fixed`).
 * A row moves whole: every statistic line and its letters, in every column;
 * the letters keep pointing at their cells. A grid of the data shown as one
 * table (`plan.siblings`), and a table without a Totals column or a single
 * column, are left as they are; so is a table nothing changes.
 */
export const arrangeRows = (model: TableModel, plan: TablePlan): TableModel => {
  if (plan.siblings || (plan.sort !== "desc" && !plan.hideEmpty)) return model
  const col = totalsColumn(model)
  if (col < 0) return model
  const units = unitsOf(withSlots(blocksOf(downRowsOf(model)), plan.layout))
  const kept = plan.hideEmpty ? units.filter((u) => !nobodyGave(u, col)) : units
  const ordered = plan.sort === "desc" ? sortRuns(kept, col) : kept
  const blocks = ordered.flatMap((u) => u.blocks)
  const rows = blocks.flatMap((b) => b.rows)
  if (rows.length === model.down.leaves.length && rows.every((row, i) => row.from === i)) return model
  // Each block is numbered again in its new place, so its rows still band together.
  const renumbered = blocks.flatMap((b, k) =>
    b.rows.map((row) =>
      row.leaf.elementIndex === undefined ? row : { ...row, leaf: { ...row.leaf, elementIndex: k } }
    )
  )
  return withDownRows(model, renumbered)
}
